import React, {useState, useEffect} from 'react';
import {
  createSelectionOptionsUtil,
  queryString,
} from '../../../Util/commonUtility';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {
  deleteCommitteeMembersById,
  getAllCommitteeMembers,
} from '../../../Store/Actions/committeeMember';
import CommitteeMemberComponent from './../../../Components/AdminComponents/CommitteeMember/index';
import {getAllChaptersByQueryStringForSearch} from '../../../Store/Actions';

const ABOUT_US_SUBTYPES = [
  {label: 'PBSS Leadership', value: 'leadership'},
  {label: 'Operations Team', value: 'operations'},
  {label: 'Distinguished Advisors', value: 'distinguish_advisor'},
  {label: 'Program Management', value: 'program_management'},
];

const parseType = (value) => (value === 'about_us' ? 'about_us' : 'chapter');

const CommitteeMemberContainer = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chaptersOptions, setChaptersOptions] = useState([]);

  const selectedType = parseType(searchParams.get('type'));
  const selectedSubtype =
    selectedType === 'chapter'
      ? searchParams.get('local_chapter_id') != null &&
        searchParams.get('local_chapter_id') !== ''
        ? Number(searchParams.get('local_chapter_id'))
        : undefined
      : searchParams.get('about_us_type') || undefined;

  const {allCommitteeMembers} = useSelector(
    ({committeeMembers}) => committeeMembers,
  );
  const {allChaptersQueryData} = useSelector(({chapters}) => chapters);
  const searchValues = useSelector(
    ({dynamicSearch}) => dynamicSearch.searchValues.committeeMembers,
  );

  const [committeeMembersData, setCommitteeMembersData] = useState();
  const [sortParams, setSortParams] = useState({
    field: 'order_number',
    order: 'ascend',
  });

  const setTabParams = (type, subtype, options) => {
    if (type === 'about_us') {
      setSearchParams(
        {type: 'about_us', about_us_type: String(subtype)},
        options,
      );
    } else {
      setSearchParams(
        {type: 'chapter', local_chapter_id: String(subtype)},
        options,
      );
    }
  };

  useEffect(() => {
    if (allCommitteeMembers) {
      setCommitteeMembersData(allCommitteeMembers?.data?.committeeMembers);
    } else if (selectedSubtype !== undefined) {
      fetchCommitteeMembers();
    }
  }, [allCommitteeMembers]);

  useEffect(() => {
    if (allChaptersQueryData?.forSearch) {
      const visibleChapters =
        allChaptersQueryData.forSearch?.data?.chapters?.filter(
          (chapter) => chapter?.is_visible_on_map === true,
        );
      setChaptersOptions(
        createSelectionOptionsUtil(visibleChapters, 'id', 'name'),
      );
    } else {
      dispatch(
        getAllChaptersByQueryStringForSearch('', '', '', {
          page: 1,
          pageCount: 100,
        }),
      );
    }
  }, [allChaptersQueryData?.forSearch?.data?.chapters]);

  // Sync URL defaults when subtype missing/invalid. Must replace, not push:
  // pushing leaves the param-less URL in history, and going back to it just
  // re-applies the defaults, so browser back gets stuck on the default tab.
  useEffect(() => {
    if (selectedType === 'chapter') {
      if (chaptersOptions?.length > 0) {
        const stillValid = chaptersOptions.some(
          (c) => String(c.value) === String(selectedSubtype),
        );
        if (!stillValid) {
          setTabParams('chapter', chaptersOptions[0].value, {replace: true});
        }
      }
    } else if (selectedType === 'about_us') {
      const stillValid = ABOUT_US_SUBTYPES.some(
        (t) => t.value === selectedSubtype,
      );
      if (!stillValid) {
        setTabParams('about_us', ABOUT_US_SUBTYPES[0].value, {replace: true});
      }
    }
  }, [selectedType, selectedSubtype, chaptersOptions]);

  useEffect(() => {
    if (selectedSubtype !== undefined && !Number.isNaN(selectedSubtype)) {
      setSortParams({field: 'order_number', order: 'ascend'});
      fetchCommitteeMembers({field: 'order_number', order: 'ascend'});
    }
  }, [selectedType, selectedSubtype]);

  useEffect(() => {
    if (
      searchValues !== undefined &&
      selectedSubtype !== undefined &&
      !Number.isNaN(selectedSubtype)
    ) {
      fetchCommitteeMembers(sortParams, searchValues);
    }
  }, [searchValues]);

  const buildSearchQuery = (values = searchValues) => {
    const base =
      selectedType === 'chapter'
        ? {type: 'chapter', local_chapter_id: selectedSubtype}
        : {type: 'about_us', about_us_type: selectedSubtype};
    // Only take name from DynamicSearch — it also stamps empty array fields that
    // would overwrite chapter / about_us tab params if merged blindly.
    const name = values?.name?.trim?.();
    return name ? {...base, name} : base;
  };

  const fetchCommitteeMembers = async (
    sort = sortParams,
    values = searchValues,
  ) => {
    try {
      const searchQueryData = queryString(buildSearchQuery(values));
      dispatch(
        getAllCommitteeMembers(
          {page: 1, pagecount: 100},
          sort.field || 'order_number',
          sort.order || 'ascend',
          searchQueryData,
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleTableChange = (_pagination, _filter, sorter) => {
    // Ant Design clears order on 3rd click; treat that as "normal" (display order).
    const nextSort = !sorter?.order
      ? {field: 'order_number', order: 'ascend'}
      : {field: sorter.field, order: sorter.order};
    setSortParams(nextSort);
    fetchCommitteeMembers(nextSort);
  };

  const deleteCommitteeMember = async (id) => {
    try {
      dispatch(deleteCommitteeMembersById(id));
    } catch (error) {
      console.error(error);
    }
  };

  const handleTypeChange = (type) => {
    if (type === 'about_us') {
      setTabParams('about_us', ABOUT_US_SUBTYPES[0].value);
    } else if (chaptersOptions?.length > 0) {
      setTabParams('chapter', chaptersOptions[0].value);
    } else {
      setSearchParams({type: 'chapter'});
    }
  };

  const handleSubtypeChange = (subtype) => {
    setTabParams(selectedType, subtype);
  };

  return (
    <div>
      <CommitteeMemberComponent
        allCommitteeMembersData={committeeMembersData}
        deleteCommitteeMember={(id) => {
          deleteCommitteeMember(id);
        }}
        chaptersOptions={chaptersOptions}
        aboutUsSubtypeOptions={ABOUT_US_SUBTYPES}
        selectedType={selectedType}
        selectedSubtype={selectedSubtype}
        onTypeChange={handleTypeChange}
        onSubtypeChange={handleSubtypeChange}
        listQueryString={searchParams.toString()}
        sortParams={sortParams}
        handleTableChange={handleTableChange}
        searchValues={searchValues}
        fetchCommitteeMembers={(values) => {
          fetchCommitteeMembers(sortParams, values);
        }}
      />
    </div>
  );
};

export default CommitteeMemberContainer;
